import type {
  BodyMeasurementFieldKey,
  BodyMeasurementValues,
} from '../../types/bodyMeasurements';
import { formatWeightKg, maskWeight, parseWeightKg } from '../../utils/masks';

export const createEmptyMeasurementValues = (): BodyMeasurementValues => ({
  weightKg: '',
  waistCm: '',
  abdomenCm: '',
  hipCm: '',
  neckCm: '',
});

/** Circumference in cm: up to 3 integer digits + optional 1 decimal (ex.: 85,5). */
export const maskCircumferenceCm = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (!digits) {
    return '';
  }

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length === 3) {
    return `${digits.slice(0, 2)},${digits.slice(2)}`;
  }

  return `${digits.slice(0, 3)},${digits.slice(3)}`;
};

export const parseCircumferenceCm = (value: string): number | null => {
  const raw = value.trim().replace(',', '.');
  if (!raw) {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 300) {
    return null;
  }

  return Math.round(parsed * 10) / 10;
};

export const formatCircumferenceCm = (value: number): string => (
  value.toFixed(1).replace('.', ',')
);

export const maskMeasurementField = (
  key: BodyMeasurementFieldKey,
  value: string,
): string => {
  if (key === 'weightKg') {
    return maskWeight(value);
  }

  return maskCircumferenceCm(value);
};

export const parseMeasurementField = (
  key: BodyMeasurementFieldKey,
  value: string,
): number | null => {
  if (key === 'weightKg') {
    return parseWeightKg(value);
  }

  return parseCircumferenceCm(value);
};

export const formatMeasurementField = (
  key: BodyMeasurementFieldKey,
  value: number,
): string => {
  if (key === 'weightKg') {
    return formatWeightKg(value);
  }

  return formatCircumferenceCm(value);
};

export const validateMeasurementValues = (
  values: BodyMeasurementValues,
): Partial<Record<BodyMeasurementFieldKey, string>> => {
  const errors: Partial<Record<BodyMeasurementFieldKey, string>> = {};

  (Object.keys(values) as BodyMeasurementFieldKey[]).forEach((key) => {
    if (!values[key].trim()) {
      errors[key] = 'Informe o valor.';
      return;
    }

    if (parseMeasurementField(key, values[key]) == null) {
      errors[key] = 'Valor inválido.';
    }
  });

  return errors;
};
